import { Divider, MenuItem, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";

type Props = {
  onCopy: (blankValues: boolean, count: number) => void;
  maxCount?: number;
};

const hasValidMaxCount = (maxCount?: number): maxCount is number => Number.isInteger(maxCount) && (maxCount as number) >= 1;

// Renders only a Menu's contents (the count field + the two copy options) — every call site keeps
// owning its own <Menu anchorEl=... open=... onClose=...> around this, since some sites need extra
// steps around it (e.g. selected-configurations.tsx's back-arrow sub-menu).
export const CopyMenu = (props: Props) => {
  const { onCopy, maxCount } = props;

  const genericTranslationsT = useGenericTranslationsT();

  // "" = cleared mid-edit; onBlur defaults to 1
  const [count, setCount] = useState<number | "">(1);

  const clampCount = (value: number) => {
    if (Number.isNaN(value)) return 1;
    return Math.min(Math.max(value, 1), hasValidMaxCount(maxCount) ? maxCount : Infinity);
  };

  const handleCopy = (blankValues: boolean) => {
    onCopy(blankValues, count === "" ? 1 : count);
  };

  return (
    <>
      <Stack px={2} py={1} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
        <TextField
          type="number"
          size="small"
          label={genericTranslationsT.numberOfCopies}
          value={count}
          onChange={e => setCount(e.target.value === "" ? "" : clampCount(Number(e.target.value)))}
          // Defaults to 1 on blur, not every keystroke, so clearing the field works
          onBlur={() => setCount(prev => (prev === "" ? 1 : prev))}
          // Selects existing text on focus so typing replaces it instead of appending
          onFocus={e => e.target.select()}
          inputProps={{ min: 1, max: hasValidMaxCount(maxCount) ? maxCount : undefined }}
          fullWidth
        />
      </Stack>
      <Divider />
      <MenuItem onClick={() => handleCopy(true)}>{genericTranslationsT.copyBlank}</MenuItem>
      <MenuItem onClick={() => handleCopy(false)}>{genericTranslationsT.copyWithValues}</MenuItem>
    </>
  );
};
